/**
 * CallSheetSection - A section with internal grid for plays
 * Sections display plays in a grid layout with color coding.
 * Supports drag-drop from play library and broken reference placeholders.
 */

import React, { useState, useCallback } from 'react'
import { MoreVertical, Plus, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import type { SectionItem, PlayItem } from './Types'
import './document-editor.css'

interface CallSheetSectionProps {
	section: SectionItem
	plays: PlayItem[]
	isSelected: boolean
	dockedEdges: { top?: boolean; right?: boolean; bottom?: boolean; left?: boolean }
	onSelect: () => void
	onAddPlay: (columnIndex: number, rowIndex: number) => void
	onRemovePlay: (playId: string) => void
	onAddColumn: () => void
	onAddRow: () => void
	onDragOver: (e: React.DragEvent) => void
	onDrop: (e: React.DragEvent, columnIndex: number, rowIndex: number) => void
}

export function CallSheetSection({
	section,
	plays,
	isSelected,
	onSelect,
	onAddPlay,
	onRemovePlay,
	onAddColumn,
	onAddRow,
	onDragOver,
	onDrop,
}: CallSheetSectionProps) {
	const [hoveredCell, setHoveredCell] = useState<{col: number; row: number} | null>(null)

	// Grid dimensions from section layout or sensible defaults
	const columns = section.gridColumns ?? 3
	const rows = section.gridRows ?? Math.max(Math.ceil(plays.length / columns) + 1, 2)

	// Build grid matrix: place plays sequentially left-to-right, top-to-bottom
	const grid: (PlayItem | null)[][] = Array(rows).fill(null).map(() => Array(columns).fill(null))
	plays.forEach((play, index) => {
		const rowIndex = Math.floor(index / columns)
		const colIndex = index % columns
		const row = grid[rowIndex]
		if (row && rowIndex < rows && colIndex < columns) {
			row[colIndex] = play
		}
	})

	const handleCellDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		onDragOver(e)
	}, [onDragOver])

	const handleCellDrop = useCallback((e: React.DragEvent, col: number, row: number) => {
		e.preventDefault()
		e.stopPropagation()
		onDrop(e, col, row)
		setHoveredCell(null)
	}, [onDrop])

	return (
		<div
			className={`callsheet-section ${isSelected ? 'callsheet-section--selected' : ''}`}
			onClick={onSelect}
			style={{
				'--section-color': section.color || 'var(--muted)',
			} as React.CSSProperties}
		>
			{/* Section header */}
			<div className="callsheet-section__header">
				<div className="callsheet-section__color-indicator" />
				<span className="callsheet-section__name">{section.name}</span>

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							className="callsheet-section__menu-button"
							onClick={(e) => e.stopPropagation()}
						>
							<MoreVertical size={14} />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={onAddColumn}>
							Add Column
						</DropdownMenuItem>
						<DropdownMenuItem onClick={onAddRow}>
							Add Row
						</DropdownMenuItem>
						<DropdownMenuItem className="text-destructive">
							Delete Section
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{/* Grid of plays */}
			<div
				className="callsheet-section__grid"
				style={{
					'--grid-columns': columns,
					'--grid-rows': rows,
				} as React.CSSProperties}
			>
				{grid.map((row, rowIndex) => (
					<React.Fragment key={rowIndex}>
						{row.map((play, colIndex) => (
							<div
								key={`${rowIndex}-${colIndex}`}
								className={`callsheet-section__cell ${
									hoveredCell?.col === colIndex && hoveredCell?.row === rowIndex
										? 'callsheet-section__cell--hover'
										: ''
								}`}
								data-has-play={play !== null}
								onDragOver={handleCellDragOver}
								onDrop={(e) => handleCellDrop(e, colIndex, rowIndex)}
								onDragEnter={() => setHoveredCell({ col: colIndex, row: rowIndex })}
								onDragLeave={() => setHoveredCell(null)}
							>
								{play ? (
									<div className={`callsheet-section__play ${play.isBrokenReference ? 'callsheet-section__play--broken' : ''}`}>
										{play.isBrokenReference && (
											<AlertTriangle size={12} className="text-destructive" />
										)}
										<span className="callsheet-section__play-name">{play.name}</span>
										{play.wristbandIncluded && !play.isBrokenReference && (
											<span className="callsheet-section__wristband-indicator" />
										)}
										{!play.isBrokenReference && (
											<Button
												variant="ghost"
												size="icon"
												className="callsheet-section__menu-button"
												onClick={(e) => {
													e.stopPropagation()
													onRemovePlay(play.blockId)
												}}
											>
												<MoreVertical size={12} />
											</Button>
										)}
									</div>
								) : (
									<button
										className="callsheet-section__add-play-button"
										onClick={(e) => {
											e.stopPropagation()
											onAddPlay(colIndex, rowIndex)
										}}
									>
										<Plus size={16} />
									</button>
								)}
							</div>
						))}
					</React.Fragment>
				))}
			</div>
		</div>
	)
}
